import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  Description,
  Input,
  Label,
  TextField,
} from "@heroui/react";
import { TriangleAlert } from "lucide-react";

import { useAuthStore } from "../stores/authStore";

import { AuthRepository } from "@/repositories/auth";
import { ThemeSwitch } from "@/components/theme-switch";

const authRepository = new AuthRepository();

export const LoginPage = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [redirectPath, setRedirectPath] = useState("/dashboard");

  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const validatePhoneNumber = (phone: string) => {
    // Basic validation: should start with + and have reasonable length
    return phone.startsWith("+") && phone.length >= 10 && phone.length <= 15;
  };

  const validateOtp = (otp: string) => {
    // OTP should be 4-6 digits
    return /^\d{4,6}$/.test(otp);
  };

  // Get redirect path from URL parameters
  useEffect(() => {
    const redirect = searchParams.get("redirect");

    if (redirect) {
      setRedirectPath(redirect);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validate inputs
    if (!validatePhoneNumber(phoneNumber)) {
      setError("Please enter a valid phone number (e.g., +971501234567)");
      setLoading(false);

      return;
    }

    if (!validateOtp(otp)) {
      setError("Please enter a valid OTP (4-6 digits)");
      setLoading(false);

      return;
    }

    try {
      const response = await authRepository.login(phoneNumber, otp);

      // Check if user is admin
      if (response.user.role !== "admin") {
        setError("Access denied. Only admin users are allowed.");

        return;
      }

      login(response.user, response.token);
      navigate(redirectPath);
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Login failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-background grid min-h-screen place-items-center p-6">
      <div className="absolute end-6 top-6 z-10">
        <ThemeSwitch />
      </div>

      <Card className="w-full max-w-100 gap-6 p-8">
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="bg-foreground text-background grid size-8 place-items-center rounded-lg text-sm font-semibold"
          >
            D
          </span>
          <span className="text-sm font-semibold">Dukkanti Admin</span>
        </div>

        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
          <p className="text-muted text-sm">
            Enter your phone number and OTP code.
          </p>
        </div>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <TextField
            className="flex flex-col gap-1.5"
            type="tel"
            value={phoneNumber}
            onChange={setPhoneNumber}
          >
            <Label>Phone Number</Label>
            <Input
              autoComplete="tel"
              placeholder="+971501234567"
              variant="secondary"
            />
          </TextField>

          <TextField
            className="flex flex-col gap-1.5"
            value={otp}
            onChange={(value) => setOtp(value.replace(/\D/g, ""))}
          >
            <Label>OTP Code</Label>
            <Input
              autoComplete="one-time-code"
              className="tabular-nums"
              inputMode="numeric"
              maxLength={6}
              placeholder="123456"
              variant="secondary"
            />
            <Description>Enter 4-6 digit OTP</Description>
          </TextField>

          {error && (
            <div
              className="bg-danger/10 text-danger flex items-center gap-2 rounded-xl p-3 text-sm"
              role="alert"
            >
              <TriangleAlert aria-hidden="true" className="size-4 shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <Button
            fullWidth
            isPending={loading}
            size="lg"
            type="submit"
            variant="primary"
          >
            Sign In
          </Button>
        </form>
      </Card>
    </div>
  );
};
